import React, { useEffect, useRef, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import IconButton from '@material-ui/core/IconButton'
import Typography from '@material-ui/core/Typography'
import AddIcon from 'components/material-symbol/icons/Add'
import RedeemIcon from 'components/material-symbol/icons/Redeem'
import CloseIcon from 'components/material-symbol/icons/Close'
import { useAuth } from 'lib/auth'
import { flags, useFeatureFlags } from 'lib/feature-flags'
import {
  getHolidaySeason,
  readHolidayBannerRecord,
  trackHolidayBannerCtaClicked,
  trackHolidayBannerDismissed,
  trackHolidayBannerViewed,
} from './analytics'
import useStyles from './styles'

export const HOLIDAY_LESSON_PACK_CTA_PATH =
  '/pga-coach/bookings/booking-types/new?offering=lesson-pack'

export const HolidayLessonPackBannerContent = ({ onCtaClick, onDismiss }) => {
  const classes = useStyles()

  return (
    <Box
      component="section"
      className={classes.banner}
      aria-labelledby="holiday-lesson-pack-banner-title"
      data-testid="holiday-lesson-pack-banner"
    >
      <Box className={classes.icon}>
        <RedeemIcon fontSize="inherit" />
      </Box>
      <Box className={classes.copy}>
        <Typography
          component="h2"
          id="holiday-lesson-pack-banner-title"
          className={classes.title}
        >
          Take advantage of the holiday season
        </Typography>
        <Typography className={classes.caption}>
          Offer Holiday Lesson Packs so golfers can give lessons as gifts or
          stock up for themselves.
        </Typography>
      </Box>
      <Button
        variant="contained"
        color="primary"
        component={RouterLink}
        to={HOLIDAY_LESSON_PACK_CTA_PATH}
        onClick={onCtaClick}
        startIcon={<AddIcon />}
        className={classes.ctaButton}
      >
        Create Lesson Pack
      </Button>
      <IconButton
        aria-label="Dismiss holiday lesson pack banner"
        size="small"
        onClick={onDismiss}
        className={classes.dismissButton}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  )
}

const HolidayLessonPackBanner = () => {
  const { user } = useAuth()
  const [isEnabled] = useFeatureFlags([
    flags.FLAG_FEAT_HOLIDAY_LESSON_PACK_BANNER,
  ])
  const season = getHolidaySeason()
  const userId = user?.id
  // Admins in Assume User mode see the coach's dashboard, but that isn't the
  // coach seeing the banner, so nothing is recorded for assumed sessions.
  const isTrackingAllowed = !user?.isAssumed
  const [isHidden, setIsHidden] = useState(() => {
    if (!season || !userId) return true
    const record = readHolidayBannerRecord(season, userId)
    return Boolean(record.dismissedAt || record.lessonPackCreatedAt)
  })
  const isVisible = Boolean(isEnabled && season && userId && !isHidden)
  const hasTrackedView = useRef(false)

  useEffect(() => {
    if (!isVisible || !isTrackingAllowed || hasTrackedView.current) return
    hasTrackedView.current = true
    trackHolidayBannerViewed({ season, userId })
  }, [isVisible, isTrackingAllowed, season, userId])

  if (!isVisible) return null

  const handleCtaClick = () => {
    if (!isTrackingAllowed) return
    trackHolidayBannerCtaClicked({
      season,
      userId,
      destination: HOLIDAY_LESSON_PACK_CTA_PATH,
    })
  }

  const handleDismiss = () => {
    if (isTrackingAllowed) trackHolidayBannerDismissed({ season, userId })
    setIsHidden(true)
  }

  return (
    <HolidayLessonPackBannerContent
      onCtaClick={handleCtaClick}
      onDismiss={handleDismiss}
    />
  )
}

export default HolidayLessonPackBanner
